'use client';

import { FC, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { FormTextField, FormAutocomplete, FormDatePicker } from '@/components/common/FormComponents';
import { FormLabel } from '@/components/common/FormLabel';
import { SampleDto, SampleActivityDto, StatusDto, ActivityCategoryDto } from '@/api/ApiClient';
import { log } from '@/lib/utils/logger';
import { nullIfBlank } from '@/lib/utils/nullIfBlank';
import { getProblemDetail } from '@/lib/utils/apiErrors';
import { useBackend } from '@/hooks/useBackend';

import { StandardDialog } from '@/components/common/StandardDialog';
import { RichTextEditor } from '@/components/common/RichTextEditor';
import { useSnackbar } from '@/hooks/useSnackbar';
import { useSelectOptions } from '@/lib/options/useSelectOptions';
import { OptionKey, KeyValueType } from '@/lib/options/optionTypes';

import { useUserInteraction } from '@/hooks/useUnsavedChanges';
import { useAuthStore } from '@/stores/authStore';
import { validateRequiredToSave } from '@/lib/utils/saveValidation';
import { createStorageActivitySaveValidation } from '@/lib/validation/saveValidationBuilders';

const OPTION_KEYS: OptionKey[] = ['researcherOptions'];

interface AddStorageDialogProps {
  open: boolean;
  onClose: () => void;
  sample: SampleDto;
  onSubmit?: (activity: SampleActivityDto) => void;
}

interface AddStorageDialogInnerProps extends AddStorageDialogProps {
  researcherOptions: KeyValueType[];
}

const AddStorageDialogInner: FC<AddStorageDialogInnerProps> = ({
  open,
  onClose,
  sample,
  onSubmit,
  researcherOptions,
}) => {
  const backend = useBackend();
  const { showSnackbar } = useSnackbar();
  const { hasUnsavedChanges, markAsInteracted } = useUserInteraction();

  const [sampleLocation, setSampleLocation] = useState('');
  const [selectedResearcher, setSelectedResearcher] = useState<KeyValueType | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [description, setDescription] = useState('');
  const [researcherError, setResearcherError] = useState(false);
  const [locationError, setLocationError] = useState(false);

  const handleClose = () => {
    setSampleLocation('');
    setSelectedResearcher(null);
    setSelectedDate(new Date());
    setDescription('');
    setResearcherError(false);
    setLocationError(false);
    onClose();
  };

  const handleSubmit = async () => {
    const saveValidation = validateRequiredToSave(
      createStorageActivitySaveValidation({
        userId: selectedResearcher?.value,
        date: selectedDate ?? undefined,
        sampleLocation,
      })
    );
    setLocationError(!!saveValidation.fieldErrors.sampleLocation);
    setResearcherError(!!saveValidation.fieldErrors.userId);

    if (!saveValidation.isValid) {
      showSnackbar('Please fill out all required fields.', 'error');
      return;
    }

    if (!backend || !sample.id) {
      showSnackbar('Backend not available or sample ID missing', 'error');
      return;
    }

    try {
      log.debug('[AddStorageDialog] Creating storage activity');

      const newActivity: SampleActivityDto = {
        sampleId: sample.id,
        category: ActivityCategoryDto.Storage,
        type: undefined,
        date: selectedDate ?? new Date(),
        status: StatusDto.Pending,
        sampleLocation: sampleLocation,
        userId: nullIfBlank(selectedResearcher?.value),
        desc: description.trim() || undefined,
      };

      const created = await backend.sampleActivity.create(newActivity);
      log.debug('[AddStorageDialog] Storage activity created:', created);

      showSnackbar('Storage information added successfully.', 'success');
      onSubmit?.(created);

      handleClose();
    } catch (error: any) {
      log.error('[AddStorageDialog] Error creating storage activity:', error);
      showSnackbar(getProblemDetail(error) ?? 'Failed to create storage activity.', 'error');
    }
  };

  return (
    <StandardDialog
      open={open}
      onClose={handleClose}
      title="Add Storage Information"
      maxWidth="md"
      onSubmit={handleSubmit}
      submitText="Add Storage Information"
      hasUnsavedChanges={hasUnsavedChanges}
      unsavedChangesMessage="You have unsaved changes. Are you sure you want to discard them?"
    >
      <FormTextField
        label="Sample Location"
        required
        fullWidth
        value={sampleLocation}
        onChange={(e) => {
          setSampleLocation(e.target.value);
          markAsInteracted();
        }}
        placeholder="e.g., Freezer A, Shelf 3, Box 2"
        size="small"
        error={locationError}
        helperText={locationError ? 'Sample location is required' : undefined}
      />

      <FormAutocomplete
        options={researcherOptions}
        getOptionLabel={(option) => option.value}
        renderOption={(props, option) => (
          <li {...props} key={option.key}>
            {option.value}
          </li>
        )}
        value={selectedResearcher}
        onChange={(_, newValue) => {
          setSelectedResearcher(newValue || null);
          markAsInteracted();
        }}
        label="Responsible Person"
        required
        error={researcherError}
        helperText={researcherError ? 'Responsible person is required' : undefined}
      />

      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <FormDatePicker
          label="Date"
          value={selectedDate}
          onChange={(newValue) => {
            setSelectedDate(newValue);
            markAsInteracted();
          }}
          required
        />
      </LocalizationProvider>

      <Box width="100%">
        <Typography variant="subtitle2" gutterBottom>
          <FormLabel optional>Description</FormLabel>
        </Typography>
        <Box width="100%">
          <RichTextEditor
            value={description}
            onChange={(value) => {
              setDescription(value);
              markAsInteracted();
            }}
          />
        </Box>
      </Box>
    </StandardDialog>
  );
};

const AddStorageDialog: FC<AddStorageDialogProps> = ({ open, onClose, sample, onSubmit }) => {
  const { options, loading: optionsLoading } = useSelectOptions(OPTION_KEYS);
  const { currentUser } = useAuthStore();

  const researcherOptions = useMemo(() => options.researcherOptions ?? [], [options.researcherOptions]);
  const dialogInitKey = `${sample?.id ?? 'sample'}|${currentUser?.id ?? 'anon'}|${currentUser?.selectedRole ?? ''}|${String(
    currentUser?.usingSignInAs ?? false
  )}`;

  const waitingMessage = !sample ? 'No sample selected.' : optionsLoading ? 'Loading options...' : null;
  if (waitingMessage) {
    return (
      <StandardDialog
        open={open}
        onClose={onClose}
        title="Add Storage Information"
        maxWidth="md"
        onSubmit={() => {}}
        submitText="Add Storage Information"
        submitDisabled
      >
        <Box sx={{ p: 2 }}>{waitingMessage}</Box>
      </StandardDialog>
    );
  }

  return (
    <AddStorageDialogInner
      key={dialogInitKey}
      open={open}
      onClose={onClose}
      sample={sample}
      onSubmit={onSubmit}
      researcherOptions={researcherOptions}
    />
  );
};

export { AddStorageDialog };
